import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GoldDots } from "@/components/Brand";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { Panel, Row, Stat } from "@/components/admin/AdminUI";

const Report = ({ r }) => (
  <div className="rounded-xl bg-alabaster p-5 mt-5 fade-in" data-testid="import-report">
    <p className="font-medium text-charcoal mb-2">{r.dry_run ? "What this file holds" : "Import finished"}</p>
    <Row label="People">{r.people}{r.already_here ? ` (${r.already_here} already here)` : ""}</Row>
    <Row label="Action Plans to bring across">{r.dry_run ? r.plans - r.plans_already_imported : r.plans}{r.dry_run && r.plans_already_imported ? ` (${r.plans_already_imported} already here)` : ""}</Row>
    {r.dry_run && r.plans_relinked > 0 && <Row label="Plans to move to the right account">{r.plans_relinked}</Row>}
    {(r.skipped_deleted_plans > 0 || r.skipped_duplicate_plans > 0) && <Row label="Left out">{r.skipped_deleted_plans} deleted by their owner · {r.skipped_duplicate_plans} duplicate</Row>}
    <Row label="Passwords carried across">{r.with_password} of {r.people}</Row>
    <Row label="Three baskets carried across">{r.with_baskets}</Row>
    {!r.dry_run && <Row label="Result">{r.created_accounts} new accounts · {r.plans_imported} plans imported{r.plans_relinked ? ` · ${r.plans_relinked} plans moved to the right account` : ""}{r.plans_already_imported ? ` · ${r.plans_already_imported} already imported` : ""}</Row>}
    {r.errors?.length > 0 && <p className="text-sm text-warn mt-3">{r.errors.length} couldn't be imported: {r.errors.join("; ")}</p>}
  </div>
);

const Importer = ({ onDone }) => {
  const api = useApi();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (dry) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dry ? "true" : "false");
    try { const { data } = await api.post("/admin/legacy-import", fd); setReport(data); if (!dry) { toast.success("go-native-2 data imported"); onDone(); } }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Panel title="Bring across go-native-2" sub="Upload the go-native-2 export file. Check it first, then import. Running it again is safe; nothing is doubled up. Each person gets one extra free plan as a thank you." testId="admin-import">
      <input ref={input} type="file" accept="application/json,.json" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setReport(null); }} data-testid="import-file-input" />
      <div className="flex flex-wrap items-center gap-3">
        <button className="btn-outline" onClick={() => input.current.click()} data-testid="import-choose-btn">{file ? "Choose a different file" : "Choose export file"}</button>
        {file && <span className="text-sm text-ink-soft truncate max-w-[260px]">{file.name}</span>}
      </div>
      {file && (
        <div className="flex flex-wrap gap-3 mt-4">
          <button className="btn-charcoal" disabled={busy} onClick={() => send(true)} data-testid="import-check-btn">{busy ? "Working" : "Check file"}</button>
          {report?.dry_run && (
            <ConfirmDialog title="Import go-native-2 now?" body={`This creates up to ${report.people - report.already_here} accounts and brings across ${report.plans - report.plans_already_imported} new plans. People sign in with their go-native-2 email and password.`}
              confirmLabel="Import" cancelLabel="Not yet" calm onConfirm={() => send(false)} testId="import-confirm"
              trigger={<button className="btn-gold" disabled={busy} data-testid="import-run-btn">Import</button>} />
          )}
        </div>
      )}
      {report && <Report r={report} />}
    </Panel>
  );
};

export const AdminData = () => {
  const api = useApi();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/data").then((r) => setD(r.data));
  useEffect(() => { load(); }, [api]); // eslint-disable-line
  const purge = async () => {
    try { const { data } = await api.post("/admin/data/purge"); toast.success(`Raw answers deleted on ${data.purged} plan(s)`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportPeople = async () => {
    try {
      const r = await api.get("/admin/data/export-people", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: "ahu-people.csv" }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <div className="py-16 flex justify-center"><GoldDots /></div>;
  return (
    <div className="space-y-5">
      <Panel title="What's stored" testId="data-stored">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Stat n={d.plans_total} label="Plans" testId="data-plans" />
          <Stat n={d.plans_with_answers} label="Plans with raw answers" testId="data-answers" />
          <Stat n={d.anonymised_plans} label="Anonymised plans" testId="data-anon" />
          <Stat n={d.migrated_plans} label="From go-native-2" testId="data-migrated" />
        </div>
      </Panel>
      <Panel title="Retention" sub="Raw answers to the ten questions are deleted 6 months after each plan is made. This runs every morning; you can also run it now." testId="data-retention">
        <Row label="Oldest raw answers">{d.oldest_answers_at ? formatDate(d.oldest_answers_at) : "None"}</Row>
        <Row label="Due for deletion now">{d.answers_due_for_deletion}</Row>
        <button className="btn-outline mt-4" onClick={purge} data-testid="purge-now-btn">Run retention now</button>
      </Panel>
      <Panel title="Export" sub="Download the people list: names, contact details, membership and plan counts. Plan content is never included." testId="data-export">
        <button className="btn-outline" onClick={exportPeople} data-testid="export-people-btn">Download people list (CSV)</button>
      </Panel>
      <Importer onDone={load} />
      {d.last_import && <p className="text-sm text-ink-soft" data-testid="last-import">Last import {formatDate(d.last_import.at)}: {d.last_import.value.created_accounts} accounts, {d.last_import.value.plans_imported} plans{d.last_import.value.plans_relinked ? `, ${d.last_import.value.plans_relinked} moved to the right account` : ""}.</p>}
    </div>
  );
};
